// Detalle/edición de una tarea EN LÍNEA dentro del panel (acordeón bajo la fila).
// Sustituye al TaskDetailModal: al ir el panel junto a "Clases de hoy" ya hay
// espacio de sobra y el modal era un brinco innecesario (pedido Carlos 2-jul).
import { useState } from 'react';
import { Flag, Check, CircleSlash, Trash2, Save, X } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { updateTask, deleteTask, Task, Assignee } from './api';
import PhotoEvidence from './PhotoEvidence';

function fmtDateTime(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export default function TaskDetailInline({ task, assignees, onClose, onPhotoChanged }: {
  task: Task; assignees: Assignee[]; onClose: (changed: boolean) => void; onPhotoChanged?: () => void;
}) {
  const toast = useToast();
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || '');
  const [due, setDue] = useState(task.complete_by || '');
  const [who, setWho] = useState(task.assigned_user_id ? String(task.assigned_user_id) : '');
  const [high, setHigh] = useState(task.priority === 'high');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const t = title.trim();
    if (!t) { toast.error('El título es obligatorio'); return; }
    setSaving(true);
    try {
      await updateTask(task.task_id, {
        title: t,
        description: description.trim() || null,
        complete_by: due || null,
        assigned_user_id: who ? Number(who) : null,
        priority: high ? 'high' : 'normal',
      });
      toast.success('Tarea actualizada');
      onClose(true);
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo guardar');
      setSaving(false);
    }
  };

  const setStatus = async (status: Task['status']) => {
    let skip_reason: string | undefined;
    if (status === 'skipped') {
      const reason = window.prompt('¿Por qué no se hizo?');
      if (reason === null) return;
      if (!reason.trim()) { toast.error('El motivo es obligatorio para omitir'); return; }
      skip_reason = reason.trim();
    }
    try {
      await updateTask(task.task_id, { status, skip_reason });
      onClose(true);
    } catch (e: any) { toast.error(e?.message || 'No se pudo actualizar'); }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar "${task.title}"?`)) return;
    try {
      await deleteTask(task.task_id);
      onClose(true);
    } catch (e: any) { toast.error(e?.message || 'No se pudo eliminar'); }
  };

  const resolved = task.status !== 'open';

  return (
    <div className="mt-1 rounded-xl border border-line bg-surface-2/60 p-3">
      {/* Estado (si ya se resolvió) */}
      {resolved && (
        <div className={`mb-2 rounded-lg px-3 py-2 text-xs ${task.status === 'done' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-600'}`}>
          {task.status === 'done' ? '✅ Completada' : '⚠️ Omitida'}
          {task.done_by_name ? ` por ${task.done_by_name}` : ''}
          {task.done_at ? ` · ${fmtDateTime(task.done_at)}` : ''}
          {task.status === 'skipped' && task.skip_reason ? <div className="mt-0.5 italic">"{task.skip_reason}"</div> : null}
        </div>
      )}

      <div className="space-y-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)}
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-accent/50" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
          placeholder="Descripción / detalle (opcional)…"
          className="w-full resize-y rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-accent/50" />
        <div className="flex flex-wrap items-center gap-2">
          <select value={who} onChange={(e) => setWho(e.target.value)} title="Responsable"
            className="max-w-[150px] rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-content-2 outline-none focus:border-accent/50">
            <option value="">Sin responsable</option>
            {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <label className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-content-3">
            Vence
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
              className="bg-transparent text-xs text-content-2 outline-none" />
          </label>
          <button type="button" onClick={() => setHigh((v) => !v)} aria-pressed={high}
            className={`flex h-8 items-center gap-1 rounded-lg border px-2 text-xs font-medium transition ${high ? 'border-rose-400 bg-rose-500/15 text-rose-600' : 'border-line text-content-3 hover:text-content'}`}>
            <Flag size={13} /> Alta
          </button>
        </div>
      </div>

      {/* Evidencia (F4): visible siempre que exista foto o que la tarea siga abierta */}
      {(task.photo_path || !resolved) && (
        <div className="mt-2 rounded-lg border border-line bg-surface/60 p-2">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-3">Foto de evidencia</p>
          <PhotoEvidence task={task} onChanged={() => onPhotoChanged?.()} />
        </div>
      )}

      {/* Acciones */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!resolved ? (
          <>
            <button onClick={() => setStatus('done')} disabled={task.requires_photo === 1 && !task.photo_path}
              title={task.requires_photo === 1 && !task.photo_path ? 'Primero toma la foto de evidencia' : 'Completar'}
              className="flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-2.5 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-40">
              <Check size={14} /> Completar
            </button>
            {task.template_id != null && (
              <button onClick={() => setStatus('skipped')}
                className="flex h-8 items-center gap-1 rounded-lg border border-amber-500/50 px-2.5 text-xs font-medium text-amber-600 transition hover:bg-amber-500/10">
                <CircleSlash size={14} /> Omitir
              </button>
            )}
          </>
        ) : (
          <button onClick={() => setStatus('open')}
            className="flex h-8 items-center gap-1 rounded-lg border border-line px-2.5 text-xs font-medium text-content-2 transition hover:bg-surface-2">
            Reabrir
          </button>
        )}
        {task.template_id == null && (
          <button onClick={remove} title="Eliminar"
            className="flex h-8 items-center gap-1 rounded-lg border border-line px-2.5 text-xs font-medium text-content-3 transition hover:border-rose-400 hover:text-rose-500">
            <Trash2 size={14} /> Eliminar
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => onClose(false)}
            className="flex h-8 items-center gap-1 rounded-lg border border-line px-2.5 text-xs font-medium text-content-3 transition hover:bg-surface-2 hover:text-content">
            <X size={14} /> Cerrar
          </button>
          <button onClick={save} disabled={saving}
            className="flex h-8 items-center gap-1 rounded-lg bg-accent px-3 text-xs font-semibold text-on-accent transition hover:opacity-90 disabled:opacity-40">
            <Save size={14} /> Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
